import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5037/api';

export default function AdminPersonMergePage() {
    const { t } = useTranslation('admin');
    const [schoolId, setSchoolId] = useState('');
    const [email, setEmail] = useState('');
    const [duplicates, setDuplicates] = useState([]);
    const [primaryId, setPrimaryId] = useState('');
    const [duplicateId, setDuplicateId] = useState('');
    const [preview, setPreview] = useState(null);
    const [entityType, setEntityType] = useState('student');
    const [message, setMessage] = useState('');

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    async function searchDuplicates() {
        if (!schoolId) return alert(t('personMerge.alerts.needSchoolId'));
        setMessage('');
        try {
            const res = await api.get('/admin/persons/students/duplicates', {
                params: { schoolId, email: email || undefined }
            });
            setDuplicates(res.data || []);
        } catch (err) {
            alert(err.response?.data?.message || t('personMerge.alerts.searchError'));
        }
    }

    async function loadPreview() {
        if (!primaryId || !duplicateId) return alert(t('personMerge.alerts.needBothIds'));
        setMessage('');
        try {
            const path = entityType === 'staff' ? 'staff' : 'students';
            const res = await api.get(`/admin/persons/${path}/merge-preview`, {
                params: { primaryId, duplicateId }
            });
            setPreview(res.data);
        } catch (err) {
            alert(err.response?.data?.message || t('personMerge.alerts.previewError'));
            setPreview(null);
        }
    }

    async function merge() {
        if (!primaryId || !duplicateId) return alert(t('personMerge.alerts.needIds'));
        if (!window.confirm(t('personMerge.alerts.confirmMerge'))) return;
        setMessage('');
        try {
            const path = entityType === 'staff' ? 'staff' : 'students';
            const res = await api.post(`/admin/persons/${path}/merge`, {
                primaryId: Number(primaryId),
                duplicateId: Number(duplicateId)
            });
            setMessage(res.data?.message || t('common:success'));
            setPreview(null);
            searchDuplicates();
        } catch (err) {
            alert(err.response?.data?.message || t('personMerge.alerts.mergeError'));
        }
    }

    return (
        <OwnerPageLayout
            title={t('personMerge.title')}
            subtitle={t('personMerge.subtitle')}
            maxWidth={900}
        >
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0 }}>{t('personMerge.searchDuplicates')}</h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <input
                        placeholder={t('personMerge.schoolId')}
                        value={schoolId}
                        onChange={(e) => setSchoolId(e.target.value)}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', width: 120 }}
                    />
                    <input
                        placeholder={t('personMerge.emailFilter')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', flex: 1, minWidth: 200 }}
                    />
                    <button onClick={searchDuplicates} style={btnStyle}>{t('personMerge.search')}</button>
                </div>
                {duplicates.length === 0 ? (
                    <p style={{ color: '#888' }}>{t('personMerge.noDuplicates')}</p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {duplicates.map((d) => (
                            <li key={d.studentId} style={cardStyle}>
                                <strong>#{d.studentId}</strong> {d.fullName}
                                <span style={{ color: '#666' }}> · {d.matchReason} · {d.status}</span>
                                {d.contactEmail && <div style={{ fontSize: 13 }}>✉ {d.contactEmail}</div>}
                                {d.userId && <div style={{ fontSize: 13 }}>👤 UserId: {d.userId}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div style={{ background: '#fff', borderRadius: 12, padding: 20 }}>
                <h3 style={{ marginTop: 0 }}>{t('personMerge.mergeSection')}</h3>
                <select value={entityType} onChange={(e) => setEntityType(e.target.value)} style={{ marginBottom: 12, padding: 8 }}>
                    <option value="student">{t('personMerge.entityStudent')}</option>
                    <option value="staff">{t('personMerge.entityStaff')}</option>
                </select>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <input
                        placeholder={t('personMerge.primaryId')}
                        value={primaryId}
                        onChange={(e) => setPrimaryId(e.target.value)}
                        style={inputStyle}
                    />
                    <input
                        placeholder={t('personMerge.duplicateId')}
                        value={duplicateId}
                        onChange={(e) => setDuplicateId(e.target.value)}
                        style={inputStyle}
                    />
                    <button onClick={loadPreview} style={btnSecondary}>{t('personMerge.preview')}</button>
                    <button onClick={merge} style={{ ...btnStyle, background: '#c62828' }}>{t('personMerge.merge')}</button>
                </div>
                {preview && (
                    <div style={{ background: '#f0f4ff', padding: 12, borderRadius: 8, fontSize: 14 }}>
                        <div>
                            <b>{t('personMerge.previewLabels.primary')}:</b> {preview.primaryName} (id {preview.primaryId}, user {preview.primaryUserId ?? t('personMerge.previewLabels.noUser')})
                        </div>
                        <div>
                            <b>{t('personMerge.previewLabels.duplicate')}:</b> {preview.duplicateName} (id {preview.duplicateId}, user {preview.duplicateUserId ?? t('personMerge.previewLabels.noUser')})
                        </div>
                        {preview.warning && <div style={{ color: '#c62828', marginTop: 8 }}>⚠ {preview.warning}</div>}
                    </div>
                )}
                {message && <p style={{ color: '#2e7d32', fontWeight: 600 }}>{message}</p>}
            </div>
        </OwnerPageLayout>
    );
}

const btnStyle = { padding: '10px 18px', background: '#EB3333', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '10px 18px', background: 'var(--sp-gray-100)', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer' };
const inputStyle = { padding: 10, borderRadius: 8, border: '1px solid #ddd', width: 180 };
const cardStyle = { padding: '10px 0', borderBottom: '1px solid #eee' };
